import { faSpinner } from '@awesome.me/kit-eee5f32034/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { router } from '@inertiajs/react';
import { Form } from '@onwardtravelsolutions/package-react/Components';
import { SessionContext } from '@onwardtravelsolutions/package-react/Contexts';
import { NoticeType } from '@onwardtravelsolutions/package-react/Enums';
import {
    useControlledField,
    useControlledRecaptchaForm,
} from '@onwardtravelsolutions/package-react/Hooks';
import { Session, Sub, User } from '@onwardtravelsolutions/package-react/Types';
import { useContext, useEffect, useRef, useState } from 'react';
import { ReCaptcha } from '../../Types';
import { SecondaryButton } from '../Buttons';
import { InputField, PasswordField } from '../Fields';
import { NoticeList } from '../Notices';

type ContactFormProps = {
    recaptcha: ReCaptcha;
    className?: string;
};

export default function LoginForm({
    recaptcha,
    className = '',
}: ContactFormProps) {
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { setUser, addGlobalNotice } = useContext(SessionContext) as Session;
    const { controller, notices, submit, response } =
        useControlledRecaptchaForm<Sub>(
            'api/forms/login',
            recaptcha.siteKey,
            500,
            'post',
            {},
            'login',
        );
    const emailControl = useControlledField(controller, 'email', {
        isRequired: true,
    });
    const passwordControl = useControlledField(controller, 'password', {
        isRequired: true,
    });

    useEffect(() => {
        if (response?.isSuccessful) {
            const user = response.data ?? null;

            if (user?.id) {
                // Set User Auth & Redirect
                setUser(user);
                addGlobalNotice({
                    title: 'Successful Login',
                    body: `You have successfully logged in as '${user.user.name}'.`,
                    type: NoticeType.Information,
                    ttl: 3000,
                    uid: 'logged_in',
                    actions: [],
                });
                router.visit('submissions', {
                    preserveState: false,
                });
            }
        } else if (response && !response.isSuccessful) {
            setIsLoading(false);
        }
    }, [response?.isSuccessful]);

    useEffect(() => {
        const removeStart = router.on('start', () => setIsLoading(true));
        const removeFinish = router.on('finish', () => setIsLoading(false));

        return () => {
            removeStart();
            removeFinish();
        };
    }, []);

    return (
        <div ref={wrapperRef} className={className}>
            <Form
                id='login-form'
                autoComplete='off'
                onSubmit={async (event) => {
                    event.preventDefault();
                    await submit();
                }}
                isSubmitted={false}
                notices={<NoticeList id='login-form' notices={notices} />}
            >
                <div className='gap-4 grid grid-cols-1 md:grid-cols-2'>
                    <InputField
                        {...{
                            id: 'email',
                            type: 'text',
                            label: 'Email',
                            placeholder: '...',
                            autoComplete: 'off',
                            ...emailControl,
                        }}
                    />
                    <PasswordField
                        {...{
                            id: 'password',
                            label: 'Password',
                            autoComplete: 'off',
                            ...passwordControl,
                        }}
                    />
                </div>

                <div className='block'>
                    <SecondaryButton
                        type='submit'
                        className='w-full'
                        disabled={isLoading}
                        tabIndex={3}
                    >
                        {isLoading ? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                fontSize={20}
                                className='m-auto text-stone-950/50 animate-spin'
                            />
                        ) : (
                            'Log In'
                        )}
                    </SecondaryButton>
                </div>
            </Form>
        </div>
    );
}
